import * as R from 'ramda';
import * as P from 'plow-js';
import { createReducer } from 'redux-act';
// constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// features
import {
  socketAvailableDriversNoteReceived,
  socketAvailableDriversColorReceived,
  socketAvailableDriversStatusReceived,
  socketAvailableDriversCommentsReceived,
  socketAvailableDriversLocationReceived,
  socketAvailableDriversReservationReceived,
} from '../sockets/actions';
// feature available-driver
import * as A from './actions';
//////////////////////////////////////////////////

const initialPagination = {
  limit: 20,
  offset: 0,
};

const commonStateProps = {
  totalCount: 0,
  itemList: null,
  loading: false,
  titleSortValues: {},
  tableTitleFilters: {},
  prevItemListGuids: [],
  pagination: initialPagination,
};

const initialState = {
  ...commonStateProps,
  truckTypes: [],
  searchRadius: '',
  locationValue: '',
  pageVisited: false,
  driverInitials: '',
  unassignedLoads: [],
  searchLocation: null,
  teamFilterValue: false,
  activeList: 'allDrivers',
  centralizedDispatch: false,
  showUnassignedLoads: false,
  // all drivers
  allDrivers: commonStateProps,
  searchDate: G.getCurrentDay(),
  availableDriversPageVisited: false,
};

const setInitialState = (state, centralizedDispatch = false) => (
  P.$set('centralizedDispatch', centralizedDispatch, initialState)
);

const setListLoading = (state, data) => (
  P.$set('loading', data, state)
);

const resetListAndPagination = (state) => (
  P.$all(
    P.$set('totalCount', 0),
    P.$set('itemList', null),
    P.$set('pagination', initialPagination),
    P.$set('prevItemListGuids', R.keys(R.pathOr({}, ['itemList'], state))),
    state,
  )
);

const getItemListSuccess = (state, { results, totalCount }) => {
  const { itemList, pagination: { limit, offset } } = state;

  const newOffset = R.add(offset, limit);

  const newList = R.mergeRight(
    itemList,
    R.indexBy(R.prop(GC.FIELD_GUID), R.map(R.assoc(GC.FIELD_SELECTED, false), results)),
  );

  return P.$all(
    P.$set('itemList', newList),
    P.$set('pageVisited', true),
    P.$set('pagination.limit', 10),
    P.$set('totalCount', totalCount),
    P.$set('availableDriversPageVisited', true),
    P.$set(
      'pagination.offset',
      G.ifElse(
        R.gt(totalCount, newOffset),
        newOffset,
        totalCount,
      ),
    ),
    state,
  );
};

const setSearchDate = (state, data) => (
  P.$set('searchDate', data, state)
);

const setTruckTypes = (state, data) => (
  P.$set('truckTypes', data, state)
);

const setSearchRadius = (state, data) => (
  P.$set('searchRadius', data, state)
);

const setLocationValue = (state, data) => (
  P.$set('locationValue', data, state)
);

const setDriverInitials = (state, data) => (
  P.$set('driverInitials', data, state)
);

const setSearchLocation = (state, data) => (
  P.$set('searchLocation', data, state)
);

const setActiveList = (state, data) => (
  P.$set('activeList', data, state)
);

const toggleTeamFilterValue = (state) => P.$toggle('teamFilterValue', state);

const toggleUnassignedLoads = (state) => P.$toggle('showUnassignedLoads', state);

const getUnassignedLoadsSuccess = (state, data) => P.$set('unassignedLoads', data, state);

const assignDriverOnAvailableDriverSuccess = (state, { telGuid }) => {
  const { unassignedLoads } = state;

  const newUnassignedLoads = R.reject(R.propEq(telGuid, GC.FIELD_GUID), unassignedLoads);

  return P.$set('unassignedLoads', newUnassignedLoads, state);
};

const setTableTitleFilter = (state, data) => {
  const { activeList, allDrivers, tableTitleFilters } = state;

  const isAllDriversList = R.equals(activeList, 'allDrivers');

  const tableTitleFiltersToUse = isAllDriversList
    ? G.getPropFromObject('tableTitleFilters', allDrivers)
    : tableTitleFilters;

  const path = G.ifElse(isAllDriversList, 'allDrivers.tableTitleFilters', 'tableTitleFilters');

  const filterValues = G.getTableTitleFilterValues(tableTitleFiltersToUse, data);

  return P.$set(path, filterValues, state);
};

const setTableTitleSort = (state, data) => {
  const { activeList, allDrivers, titleSortValues } = state;

  const isAllDriversList = R.equals(activeList, 'allDrivers');

  const titleSortValuesToUse = isAllDriversList
    ? G.getPropFromObject('titleSortValues', allDrivers)
    : titleSortValues;

  const path = G.ifElse(isAllDriversList, 'allDrivers.titleSortValues', 'titleSortValues');

  const sortValues = G.getTableTitleSortValues(titleSortValuesToUse, data);

  return P.$set(path, sortValues, state);
};

const updateTelCustomStatusInList = ({ telGuid, itemList, newCustomStatus }) => R.mapObjIndexed(
  (item) => {
    const tel = G.getPropFromObject(GC.FIELD_TEL, item);

    if (R.propEq(telGuid, GC.FIELD_GUID, tel)) {
      return R.assocPath([GC.FIELD_TEL, GC.FIELD_CUSTOM_STATUS], newCustomStatus, item);
    }

    return item;
  },
  R.or(itemList, {}),
);

const updateTelCustomStatusSuccess = (state, { guid: telGuid, data: newCustomStatus }) => {
  const { itemList, activeList, allDrivers: { itemList: allDriversList } } = state;

  const newItemList = updateTelCustomStatusInList({ telGuid, itemList, newCustomStatus });

  if (R.equals(activeList, 'availableDrivers')) {
    return P.$set('itemList', newItemList, state);
  }

  const newAllDriversList = updateTelCustomStatusInList({ telGuid, newCustomStatus, itemList: allDriversList });

  if (G.isNilOrEmpty(itemList)) {
    return P.$set('allDrivers.itemList', newAllDriversList, state);
  }

  return P.$all(
    P.$set('itemList', newItemList),
    P.$set('allDrivers.itemList', newAllDriversList),
    state,
  );
};

const getDriverPathsAndCommonInfo = (state, driverGuid) => {
  const driverPath = `itemList.${driverGuid}`;
  const driverFromAllDriversListPath = `allDrivers.${driverPath}`;

  const driver = P.$get(driverPath, state);
  const driverFromAllDriversList = P.$get(driverFromAllDriversListPath, state);

  const isDriverMissing = G.isNilOrEmpty(driver);
  const isDriverFromAllDriversListMissing = G.isNilOrEmpty(driverFromAllDriversList);
  const isDriversDataMissing = R.and(isDriverMissing, isDriverFromAllDriversListMissing);

  return {
    driver,
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversList,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  };
};

const getAvailableDriverByGuidSuccess = (state, { data, driverGuid }) => {
  const {
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (R.or(G.isNilOrEmpty(data), isDriversDataMissing)) return state;

  if (isDriverMissing) return P.$set(driverFromAllDriversListPath, data, state);

  if (isDriverFromAllDriversListMissing) return P.$set(driverPath, data, state);

  return P.$all(
    P.$set(driverPath, data),
    P.$set(driverFromAllDriversListPath, data),
    state,
  );
};

const selectItem = (state, data) => {
  const { itemList, activeList, allDrivers: { itemList: allDriversList } } = state;

  const isAllDriversList = R.equals(activeList, 'allDrivers');

  if (R.equals(data, 'all')) {
    const activeItemList = isAllDriversList ? allDriversList : itemList;

    const value = R.not(R.all(R.prop(GC.FIELD_SELECTED), R.values(activeItemList)));

    return P.$set(
      isAllDriversList ? 'allDrivers.itemList' : 'itemList',
      R.map((item) => R.assoc(GC.FIELD_SELECTED, value, item), activeItemList),
      state,
    );
  }

  const { driverPath, driverFromAllDriversListPath } = getDriverPathsAndCommonInfo(state, data);

  const path = isAllDriversList ? driverFromAllDriversListPath : driverPath;

  return P.$toggle(`${path}.${GC.FIELD_SELECTED}`, state);
};

const setDriverComments = (state, { data, driverGuid }) => {
  const {
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  if (isDriverMissing) return P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_DRIVER_COMMENTS}`, data, state);

  if (isDriverFromAllDriversListMissing) return P.$set(`${driverPath}.${GC.FIELD_DRIVER_COMMENTS}`, data, state);

  return P.$all(
    P.$set(`${driverPath}.${GC.FIELD_DRIVER_COMMENTS}`, data),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_DRIVER_COMMENTS}`, data),
    state,
  );
};

// all drivers
const getAllDriversListSuccess = (state, { results, totalCount }) => {
  const { allDrivers: { itemList, pagination } } = state;

  const { limit, offset } = pagination;

  const newOffset = R.add(offset, limit);

  const newList = R.mergeRight(
    itemList,
    R.indexBy(R.prop(GC.FIELD_GUID), R.map(R.assoc(GC.FIELD_SELECTED, false), results)),
  );

  return P.$all(
    P.$set('pageVisited', true),
    P.$set('allDrivers.itemList', newList),
    P.$set('allDrivers.pagination.limit', 10),
    P.$set('allDrivers.totalCount', totalCount),
    P.$set(
      'allDrivers.pagination.offset',
      G.ifElse(
        R.gt(totalCount, newOffset),
        newOffset,
        totalCount,
      ),
    ),
    state,
  );
};

const setAllDriversListLoading = (state, data) => (
  P.$set('allDrivers.loading', data, state)
);

const resetAllDriversListAndPagination = (state, shouldResetPrevItemListGuids) => (
  P.$all(
    P.$set('allDrivers.totalCount', 0),
    P.$set('allDrivers.itemList', null),
    P.$set('allDrivers.pagination', initialPagination),
    P.$set(
      'allDrivers.prevItemListGuids',
      shouldResetPrevItemListGuids ? [] : R.keys(R.pathOr({}, ['allDrivers', 'itemList'], state)),
    ),
    state,
  )
);

// sockets
const handleWSAvailableDriversReservationReceived = (state, { data, driverGuid }) => {
  const {
    driver,
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversList,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  const newData = R.omit([GC.FIELD_NOTE], data);

  const newDriverFromAllDriversList = isDriverFromAllDriversListMissing
    ? {}
    : R.mergeRight(driverFromAllDriversList, newData);

  if (isDriverMissing) return P.$set(driverFromAllDriversListPath, newDriverFromAllDriversList, state);

  const newDriver = R.mergeRight(driver, newData);

  if (isDriverFromAllDriversListMissing) return P.$set(driverPath, newDriver, state);

  return P.$all(
    P.$set(driverPath, newDriver),
    P.$set(driverFromAllDriversListPath, newDriverFromAllDriversList),
    state,
  );
};

const handleWSAvailableDriversNoteReceived = (state, { data, driverGuid }) => {
  const {
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  if (isDriverMissing) {
    return P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_NOTE}`, data, state);
  }

  if (isDriverFromAllDriversListMissing) {
    return P.$set(`${driverPath}.${GC.FIELD_NOTE}`, data, state);
  }

  return P.$all(
    P.$set(`${driverPath}.${GC.FIELD_NOTE}`, data),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_NOTE}`, data),
    state,
  );
};

const handleWSAvailableDriversColorReceived = (state, { data, driverGuid }) => {
  const {
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  const color = R.pathOr(null, [GC.FIELD_COLOR], data);

  if (isDriverMissing) {
    return P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_AVAILABLE_DRIVERS_COLOR}`, color, state);
  }

  if (isDriverFromAllDriversListMissing) {
    return P.$set(`${driverPath}.${GC.FIELD_AVAILABLE_DRIVERS_COLOR}`, color, state);
  }

  return P.$all(
    P.$set(`${driverPath}.${GC.FIELD_AVAILABLE_DRIVERS_COLOR}`, color),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_AVAILABLE_DRIVERS_COLOR}`, color),
    state,
  );
};

const handleWSAvailableDriversCommentsReceived = (state, { driverGuid, data: { eta, comments, trailerComments } }) => {
  const {
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  if (isDriverMissing) {
    return P.$all(
      P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_ETA}`, eta),
      P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_COMMENTS}`, comments),
      P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_TRAILER_COMMENTS}`, trailerComments),
      state,
    );
  }

  if (isDriverFromAllDriversListMissing) {
    return P.$all(
      P.$set(`${driverPath}.${GC.FIELD_ETA}`, eta),
      P.$set(`${driverPath}.${GC.FIELD_COMMENTS}`, comments),
      P.$set(`${driverPath}.${GC.FIELD_TRAILER_COMMENTS}`, trailerComments),
      state,
    );
  }

  return P.$all(
    P.$set(`${driverPath}.${GC.FIELD_ETA}`, eta),
    P.$set(`${driverPath}.${GC.FIELD_COMMENTS}`, comments),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_ETA}`, eta),
    P.$set(`${driverPath}.${GC.FIELD_TRAILER_COMMENTS}`, trailerComments),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_COMMENTS}`, comments),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_TRAILER_COMMENTS}`, trailerComments),
    state,
  );
};

const handleWSAvailableDriversLocationReceived = (state, { data, driverGuid }) => {
  const {
    driver,
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  if (isDriverMissing) {
    return P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_LOCATION}`, data, state);
  }

  const { searchLocation } = state;

  let distance = R.path([GC.FIELD_DISTANCE], driver);

  if (R.and(G.isNotNilAndNotEmpty(searchLocation), G.isNilOrEmpty(R.path([GC.FIELD_TEL], driver)))) {
    distance = G.mathRoundNumber(G.getDistanceBetweenTwoPoints(searchLocation, data));
  }

  if (isDriverFromAllDriversListMissing) {
    return P.$all(
      P.$set(`${driverPath}.${GC.FIELD_LOCATION}`, data),
      P.$set(`${driverPath}.${GC.FIELD_DISTANCE}`, distance),
      state,
    );
  }

  return P.$all(
    P.$set(`${driverPath}.${GC.FIELD_LOCATION}`, data),
    P.$set(`${driverPath}.${GC.FIELD_DISTANCE}`, distance),
    P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_LOCATION}`, data),
    state,
  );
};

const handleWSAvailableDriversStatusReceived = (state, { data, driverGuid }) => {
  const {
    driver,
    driverPath,
    isDriverMissing,
    isDriversDataMissing,
    driverFromAllDriversList,
    driverFromAllDriversListPath,
    isDriverFromAllDriversListMissing,
  } = getDriverPathsAndCommonInfo(state, driverGuid);

  if (isDriversDataMissing) return state;

  const { searchLocation } = state;

  const { status, statusSource, availableAtDate, availableAtLocation } = data;

  if (G.isNotNilAndNotEmpty(R.path([GC.FIELD_TEL], R.or(driver, driverFromAllDriversList)))) {
    if (isDriverMissing) {
      return P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_STATUS}`, status, state);
    }

    if (isDriverFromAllDriversListMissing) {
      return P.$set(`${driverPath}.${GC.FIELD_STATUS}`, status, state);
    }

    return P.$all(
      P.$set(`${driverPath}.${GC.FIELD_STATUS}`, status),
      P.$set(`${driverFromAllDriversListPath}.${GC.FIELD_STATUS}`, status),
      state,
    );
  }

  const newDriverFromAllDriversList = isDriverFromAllDriversListMissing
    ? {}
    : {
      ...driverFromAllDriversList,
      status,
      statusSource,
      [GC.FIELD_FUTURE_AVAILABILITY_DATE]: availableAtDate,
      [GC.FIELD_FUTURE_AVAILABILITY_LOCATION]: availableAtLocation,
    };

  if (isDriverMissing) return P.$set(driverFromAllDriversListPath, newDriverFromAllDriversList, state);

  let distance = R.path([GC.FIELD_DISTANCE], driver);

  if (G.isNotNilAndNotEmpty(searchLocation)) {
    if (R.equals(status, ENUMS.ENUM_AVAILABLE_IN_FUTURE)) {
      distance = G.mathRoundNumber(G.getDistanceBetweenTwoPoints(searchLocation, availableAtLocation));
    }

    const { futureAvailabilityLocation } = driver;

    if (R.and(R.equals(status, ENUMS.ENUM_UNAVAILABLE), G.isNotNilAndNotEmpty(futureAvailabilityLocation))) {
      const { location } = driver;

      distance = G.mathRoundNumber(G.getDistanceBetweenTwoPoints(searchLocation, location));
    }
  }

  const newDriver = {
    ...driver,
    status,
    distance,
    statusSource,
    [GC.FIELD_FUTURE_AVAILABILITY_DATE]: availableAtDate,
    [GC.FIELD_FUTURE_AVAILABILITY_LOCATION]: availableAtLocation,
  };

  if (isDriverFromAllDriversListMissing) return P.$set(driverPath, newDriver, state);

  return P.$all(
    P.$set(driverPath, newDriver),
    P.$set(driverFromAllDriversListPath, newDriverFromAllDriversList),
    state,
  );
};

export default createReducer({
  [A.selectItem]: selectItem,
  [A.setSearchDate]: setSearchDate,
  [A.setTruckTypes]: setTruckTypes,
  [A.setActiveList]: setActiveList,
  [A.setListLoading]: setListLoading,
  [A.setSearchRadius]: setSearchRadius,
  [A.setInitialState]: setInitialState,
  [A.setLocationValue]: setLocationValue,
  [A.setTableTitleSort]: setTableTitleSort,
  [A.setDriverInitials]: setDriverInitials,
  [A.setSearchLocation]: setSearchLocation,
  [A.setDriverComments]: setDriverComments,
  [A.getItemListSuccess]: getItemListSuccess,
  [A.setTableTitleFilter]: setTableTitleFilter,
  [A.toggleTeamFilterValue]: toggleTeamFilterValue,
  [A.toggleUnassignedLoads]: toggleUnassignedLoads,
  [A.resetListAndPagination]: resetListAndPagination,
  [A.getUnassignedLoadsSuccess]: getUnassignedLoadsSuccess,
  [A.updateTelCustomStatusSuccess]: updateTelCustomStatusSuccess,
  [A.getAvailableDriverByGuidSuccess]: getAvailableDriverByGuidSuccess,
  [A.assignDriverOnAvailableDriverSuccess]: assignDriverOnAvailableDriverSuccess,
  // all drivers
  [A.setAllDriversListLoading]: setAllDriversListLoading,
  [A.getAllDriversListSuccess]: getAllDriversListSuccess,
  [A.resetAllDriversListAndPagination]: resetAllDriversListAndPagination,
  // sockets
  [socketAvailableDriversNoteReceived]: handleWSAvailableDriversNoteReceived,
  [socketAvailableDriversColorReceived]: handleWSAvailableDriversColorReceived,
  [socketAvailableDriversStatusReceived]: handleWSAvailableDriversStatusReceived,
  [socketAvailableDriversCommentsReceived]: handleWSAvailableDriversCommentsReceived,
  [socketAvailableDriversLocationReceived]: handleWSAvailableDriversLocationReceived,
  [socketAvailableDriversReservationReceived]: handleWSAvailableDriversReservationReceived,
}, initialState);
